/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTheme } from '@material-ui/core';
import type { CSSProperties } from 'react';
import { ScoreSuccessEnum } from '../../api';
import { scoreToColorConverter } from '../../helpers/scoreToColorConverter';
import { percentToScoreSuccess } from '../../helpers/percentToScoreSuccess';

export interface ScoreBarProps {
  /** Score percentage 0–100, or null when the score is not yet measured. */
  scorePercent: number | null | undefined;
  /**
   * Explicit health level. When omitted it is derived from `scorePercent`
   * via {@link percentToScoreSuccess} so the bar/number is always tinted by
   * health rather than a fixed ink colour.
   */
  scoreSuccess?: ScoreSuccessEnum | null;
  /** Optional label used for the accessible name (e.g. the pillar title). */
  label?: string;
  /** Overall width of the bar; defaults to filling its container. */
  width?: number | string;
  /** Track (and fill) height; defaults to `0.5rem`. */
  height?: number | string;
  /** Font size of the health-tinted number; defaults to `0.875rem`. */
  numberFontSize?: number | string;
  /**
   * Fixed width of the number slot. When set, the number never sizes the bar:
   * every row's track ends at the same x, so bars and numbers line up down a
   * column. Defaults to a content-sized slot of at least `2.75rem`.
   */
  numberWidth?: number | string;
  /** Minimum width of the track, so a fluid bar never collapses to a sliver. */
  trackMinWidth?: number | string;
}

const clampPercent = (value: number): number =>
  Math.max(0, Math.min(100, value));

/**
 * A horizontal score bar whose fill width tracks the score percentage and
 * whose colour tracks health (green/amber/red) via the plugin theme's
 * {@link scoreToColorConverter} — never a hard-coded hex. Theme-aware: the
 * track uses `theme.palette.divider`, so it reads correctly on light and dark.
 * Unmeasured scores (`scorePercent == null`) render a muted ghost "n/a".
 *
 * The percentage is also rendered as a health-tinted number (never plain ink),
 * matching the "coloured score number" treatment from the board redesign.
 *
 * Shared primitive: the board (task 4) and per-System view (tasks 5–6) build on
 * this component and {@link scoreToColorConverter}.
 */
export const ScoreBar = ({
  scorePercent,
  scoreSuccess,
  label,
  width,
  height = '0.5rem',
  numberFontSize = '0.875rem',
  numberWidth,
  trackMinWidth,
}: ScoreBarProps) => {
  const theme = useTheme();
  const measured = scorePercent !== null && typeof scorePercent !== 'undefined';
  const success = scoreSuccess ?? percentToScoreSuccess(scorePercent);
  const color = scoreToColorConverter(success ?? undefined);

  const rootStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: width ?? '100%',
    minWidth: '5rem',
  };

  const trackStyle: CSSProperties = {
    position: 'relative',
    flex: 1,
    minWidth: trackMinWidth,
    height,
    borderRadius: '0.25rem',
    backgroundColor: theme.palette.divider,
    overflow: 'hidden',
  };

  const fillStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: `${measured ? clampPercent(scorePercent as number) : 0}%`,
    backgroundColor: color,
    borderRadius: 'inherit',
    transition: 'width 0.3s ease',
  };

  const numberStyle: CSSProperties = {
    ...(numberWidth !== undefined
      ? { width: numberWidth, flexShrink: 0 }
      : { minWidth: '2.75rem' }),
    textAlign: 'right',
    fontWeight: 700,
    fontSize: numberFontSize,
    // Health-tinted number when measured; muted neutral for "n/a".
    color: measured ? color : theme.palette.text.disabled,
    fontVariantNumeric: 'tabular-nums',
  };

  const accessibleName = measured
    ? `${label ? `${label} ` : ''}score ${clampPercent(
        scorePercent as number,
      )} percent`
    : `${label ? `${label} ` : ''}not yet measured`;

  return (
    <div
      style={rootStyle}
      data-testid="score-bar"
      data-measured={measured ? 'true' : 'false'}
      data-score-success={success ?? 'none'}
      role="img"
      aria-label={accessibleName}
    >
      <span style={trackStyle} data-testid="score-bar-track">
        {measured && <span style={fillStyle} data-testid="score-bar-fill" />}
      </span>
      <span style={numberStyle} data-testid="score-bar-number">
        {measured ? `${clampPercent(scorePercent as number)} %` : 'n/a'}
      </span>
    </div>
  );
};
